/**
 * The realistic cow's mesh, at three levels of detail (QualityTier.cowLod).
 *
 * Built from primitives in the physics body frame (x toward the head, y up,
 * z across), so the physics quaternion orients it directly. The torso is
 * exactly the physics ellipsoid; head, legs, ears, tail, and udder are
 * decoration and do not affect the physics.
 *
 * - LOD 0: smooth, all parts.
 * - LOD 1: fewer segments, no ears or tail.
 * - LOD 2: boxes. Deliberately still cow-shaped, so realistic mode never
 *   looks like the spherical cow, even on the low tier.
 *
 * Shown only in realistic mode; the spherical and vacuum modes keep the
 * sphere in scene.ts at every tier. Scaled by the same display
 * exaggeration as the sphere (physical size is invisible at storm scale).
 */
import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  SphereGeometry,
  type BufferGeometry,
  type CanvasTexture,
  type Material,
} from 'three/webgpu';
import type { QualityTier } from '../config/tiers';
import type { EllipsoidCowParams } from '../physics/realisticCow';

export interface CowLodHandle {
  readonly root: Group;
  /** Display scale, km per physical metre. */
  readonly kmPerM: number;
  setLod(lod: QualityTier['cowLod']): void;
  setVisible(v: boolean): void;
  dispose(): void;
}

export function createCowLod(
  p: EllipsoidCowParams,
  lod: QualityTier['cowLod'],
  kmPerM: number,
  texture: CanvasTexture,
): CowLodHandle {
  const root = new Group();
  const hide = new MeshStandardNodeMaterial({ map: texture, roughness: 0.7 });
  const dark = new MeshStandardNodeMaterial({ color: 0x2a2522, roughness: 0.8 });
  const pink = new MeshStandardNodeMaterial({ color: 0xe8a3a8, roughness: 0.6 });
  const geos: BufferGeometry[] = [];
  const mats: Material[] = [hide, dark, pink];

  // Body-frame dimensions in km.
  const ax = p.ax * kmPerM;
  const ay = p.ay * kmPerM;
  const az = p.az * kmPerM;

  function part(
    g: BufferGeometry,
    m: Material,
    x: number,
    y: number,
    z: number,
    parent: Group,
  ): Mesh {
    geos.push(g);
    const mesh = new Mesh(g, m);
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  }

  function smooth(seg: number, extras: boolean): Group {
    const g = new Group();
    const torso = part(new SphereGeometry(1, seg, Math.max(6, seg >> 1)), hide, 0, 0, 0, g);
    torso.scale.set(ax, ay, az);
    const head = part(
      new SphereGeometry(1, seg, Math.max(6, seg >> 1)),
      hide,
      ax * 1.05,
      ay * 0.45,
      0,
      g,
    );
    head.scale.set(ax * 0.32, ay * 0.42, az * 0.7);
    const muzzle = part(
      new SphereGeometry(1, seg, Math.max(6, seg >> 1)),
      pink,
      ax * 1.33,
      ay * 0.32,
      0,
      g,
    );
    muzzle.scale.set(ax * 0.12, ay * 0.22, az * 0.55);
    const legGeo = (): CylinderGeometry =>
      new CylinderGeometry(az * 0.22, az * 0.18, ay * 1.1, Math.max(5, seg >> 1));
    for (const [lx, lz] of [
      [0.6, 0.55],
      [0.6, -0.55],
      [-0.6, 0.55],
      [-0.6, -0.55],
    ] as const) {
      part(legGeo(), hide, ax * lx, -ay * 1.05, az * lz, g);
      part(
        new CylinderGeometry(az * 0.2, az * 0.2, ay * 0.12, Math.max(5, seg >> 1)),
        dark,
        ax * lx,
        -ay * 1.63,
        az * lz,
        g,
      );
    }
    const udder = part(
      new SphereGeometry(1, seg, Math.max(6, seg >> 1)),
      pink,
      -ax * 0.35,
      -ay * 0.85,
      0,
      g,
    );
    udder.scale.set(ax * 0.18, ay * 0.22, az * 0.45);
    if (extras) {
      for (const ez of [1, -1]) {
        const ear = part(new SphereGeometry(1, 8, 6), hide, ax * 1.0, ay * 0.82, az * 0.72 * ez, g);
        ear.scale.set(ax * 0.06, ay * 0.1, az * 0.32);
        const horn = part(
          new CylinderGeometry(0, az * 0.08, ay * 0.3, 6),
          dark,
          ax * 1.02,
          ay * 0.95,
          az * 0.4 * ez,
          g,
        );
        horn.rotation.x = 0.4 * ez;
      }
      const tail = part(
        new CylinderGeometry(az * 0.05, az * 0.05, ay * 1.2, 6),
        hide,
        -ax * 1.02,
        -ay * 0.1,
        0,
        g,
      );
      tail.rotation.z = 0.25;
      part(new SphereGeometry(az * 0.13, 8, 6), dark, -ax * 1.12, -ay * 0.7, 0, g);
    }
    return g;
  }

  function blocky(): Group {
    const g = new Group();
    part(new BoxGeometry(ax * 2, ay * 1.6, az * 2), hide, 0, 0, 0, g);
    part(new BoxGeometry(ax * 0.55, ay * 0.75, az * 1.2), hide, ax * 1.2, ay * 0.4, 0, g);
    for (const [lx, lz] of [
      [0.6, 0.55],
      [0.6, -0.55],
      [-0.6, 0.55],
      [-0.6, -0.55],
    ] as const) {
      part(new BoxGeometry(az * 0.4, ay * 1.2, az * 0.4), hide, ax * lx, -ay * 1.2, az * lz, g);
    }
    return g;
  }

  const levels = [smooth(28, true), smooth(10, false), blocky()] as const;
  for (const l of levels) root.add(l);

  function setLod(next: QualityTier['cowLod']): void {
    levels.forEach((l, i) => {
      l.visible = i === next;
    });
  }
  setLod(lod);

  return {
    root,
    kmPerM,
    setLod,
    setVisible(v: boolean): void {
      root.visible = v;
    },
    dispose(): void {
      for (const g of geos) g.dispose();
      for (const m of mats) m.dispose();
    },
  };
}
